import { supabase } from '../../core/config/supabase';
import { Participant } from '../../domain/models';
import { ParticipantRepository } from '../../domain/repositories';
import { ParticipantDataSource } from '../datasources';
import { participantEntityToModel, participantModelToEntity } from '../entities';
import { logger } from '@/src/core/utils';

export class ParticipantRepositoryImpl implements ParticipantRepository {
  constructor(private dataSource: ParticipantDataSource) {}

  async create(participant: Omit<Participant, 'id' | 'createdAt' | 'updatedAt'>): Promise<Participant> {
    const entity = participantModelToEntity(participant);
    const createdEntity = await this.dataSource.create(entity as any);
    return participantEntityToModel(createdEntity);
  }

  async getById(id: string): Promise<Participant | null> {
    const entity = await this.dataSource.getById(id);
    if (!entity) return null;
    return participantEntityToModel(entity);
  }

  async getByTournamentId(tournamentId: string): Promise<Participant[]> {
    const entities = await this.dataSource.getByTournamentId(tournamentId);
    
    // Get user info for registered participants
    const participantsWithUserInfo = await Promise.all(
      entities.map(async (entity) => {
        if (entity.user_id) {
          try {
            const { data } = await supabase.rpc('search_user_by_email_by_id', {
              user_id: entity.user_id
            });
            
            if (data && data.length > 0) {
              return participantEntityToModel(entity, data[0].email);
            }
          } catch (err) {
            logger.error('ParticipantRepo', 'Error fetching user info', { error: err });
          }
        }
        
        return participantEntityToModel(entity);
      })
    );
    
    return participantsWithUserInfo;
  }
  
  async getByUserId(userId: string): Promise<Participant[]> {
    const entities = await this.dataSource.getByUserId(userId);
    
    // Get user info for registered participants
    const participantsWithUserInfo = await Promise.all(
      entities.map(async (entity) => {
        if (entity.user_id) {
          try {
            const { data } = await supabase.rpc('search_user_by_email_by_id', {
              user_id: entity.user_id
            });
            
            if (data && data.length > 0) {
              return participantEntityToModel(entity, data[0].email);
            }
          } catch (err) {
            logger.error('ParticipantRepo', 'Error fetching user info', { error: err });
          }
        }
        
        return participantEntityToModel(entity);
      })
    );
    
    return participantsWithUserInfo;
  }
  
  async update(id: string, participant: Partial<Participant>): Promise<Participant> {
    const entity = participantModelToEntity(participant);
    const updatedEntity = await this.dataSource.update(id, entity);
    return participantEntityToModel(updatedEntity);
  }

  async delete(id: string): Promise<void> {
    await this.dataSource.delete(id);
  }
}
